import { useState, useEffect, useRef } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

const productCategories = [
  { key: 'mortadelle', labelKey: 'nav.productCategories.mortadelle' },
  { key: 'prosciuttiCrudi', labelKey: 'nav.productCategories.prosciuttiCrudi' },
  { key: 'prosciuttiCotti', labelKey: 'nav.productCategories.prosciuttiCotti' },
  { key: 'salami', labelKey: 'nav.productCategories.salami' },
  { key: 'arrosti', labelKey: 'nav.productCategories.arrosti' },
  { key: 'specialitaStagionate', labelKey: 'nav.productCategories.specialitaStagionate' },
  { key: 'affettati', labelKey: 'nav.productCategories.affettati' },
  { key: 'wurstel', labelKey: 'nav.productCategories.wurstel' },
  { key: 'cubettiTranci', labelKey: 'nav.productCategories.cubettiTranci' },
  { key: 'precotti', labelKey: 'nav.productCategories.precotti' },
]

const categoryToProductSlug: Record<string, string> = {
  mortadelle: 'mortadella',
  prosciuttiCrudi: 'prosciutto-parma',
  prosciuttiCotti: 'prosciutto',
  salami: 'salami',
  arrosti: 'arrosti',
  specialitaStagionate: 'guanciale',
  affettati: 'affettati',
  wurstel: 'wurstel',
  cubettiTranci: 'cubetti-tranci',
  precotti: 'precotti',
}

const navItems: { key: RouteKey; labelKey: string; hasDropdown?: boolean }[] = [
  { key: 'products', labelKey: 'nav.products', hasDropdown: true },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'recipes', labelKey: 'nav.recipes' },
  { key: 'news', labelKey: 'nav.news' },
  { key: 'nutrition', labelKey: 'nav.nutrition' },
  { key: 'workWithUs', labelKey: 'nav.workWithUs' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t, i18n } = useTranslation('common')
  const location = useLocation()
  const lang = i18n.language as SupportedLanguage
  const productsPath = localizedRoutes.products[lang]
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false)
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileOpen(false)
    setIsDropdownOpen(false)
    setMobileProductsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileOpen])

  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
      dropdownTimeoutRef.current = null
    }
    setIsDropdownOpen(true)
  }

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false)
    }, 200)
  }

  // Pages with light-colored hero backgrounds need dark header text even at top
  const hasLightHero = pathSegments.length >= 2 && (
    currentSlug === (localizedRoutes.privacy?.[lang] || 'privacy-policy') ||
    // Product detail pages: anything nested under products path
    pathSegments.length >= 3
  )
  const isTransparent = !isScrolled && !isMobileOpen && !hasLightHero

  const isActiveRoute = (key: RouteKey) => {
    const routePath = localizedRoutes[key]
    return (
      routePath.it === currentSlug ||
      routePath.en === currentSlug ||
      (key === 'home' && currentSlug === '')
    )
  }

  const CategoryLink = ({ category }: { category: typeof productCategories[number] }) => {
    const slug = categoryToProductSlug[category.key]
    const className = "group flex items-center gap-3 py-2.5 transition-colors duration-200"

    const content = (
      <>
        <span className="h-px w-0 bg-[#DF631E] transition-all duration-300 group-hover:w-4" />
        <span className="text-sm text-[#4E2118]/60 group-hover:text-[#4E2118] transition-colors duration-200">
          {t(category.labelKey)}
        </span>
      </>
    )

    return slug ? (
      <Link
        to={`/${lang}/${productsPath}/${slug}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={className}
      >
        {content}
      </Link>
    ) : (
      <LocalizedLink to="products" className={className}>
        {content}
      </LocalizedLink>
    )
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/98 backdrop-blur-md shadow-lg'
      )}
    >
      <a href="#main-content" className="skip-link">
        {t('aria.skipToContent')}
      </a>

      <nav
        aria-label={t('aria.mainNavigation')}
        className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-20 md:h-24"
      >
        {/* Logo */}
        <LocalizedLink to="home" className="flex-shrink-0 flex items-center">
          <img
            src="/images/fiorucci-logo.svg"
            alt="Fiorucci Salumi"
            className="h-16 md:h-20 w-auto"
          />
        </LocalizedLink>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map(({ key, labelKey, hasDropdown }) => (
            <div
              key={key}
              className="relative"
              onMouseEnter={hasDropdown ? handleDropdownEnter : undefined}
              onMouseLeave={hasDropdown ? handleDropdownLeave : undefined}
            >
              <LocalizedLink
                to={key}
                className={cn(
                  'group relative px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center gap-1',
                  isTransparent
                    ? 'text-white/90 hover:text-white'
                    : 'text-[#4E2118] hover:text-[#4E2118]',
                  isActiveRoute(key) && 'font-semibold'
                )}
              >
                {t(labelKey)}
                {hasDropdown && (
                  <ChevronDown
                    className={cn(
                      'w-3.5 h-3.5 transition-transform duration-300',
                      isDropdownOpen && 'rotate-180'
                    )}
                  />
                )}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-px transition-all duration-300',
                    isActiveRoute(key)
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100',
                    isTransparent ? 'bg-white' : 'bg-[#DF631E]'
                  )}
                />
              </LocalizedLink>

              {/* Dropdown under Prodotti */}
              {hasDropdown && (
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                      className="absolute top-full left-0 mt-3 w-[520px] bg-white shadow-2xl shadow-black/10 border border-[#4E2118]/8 overflow-hidden z-50"
                      onMouseEnter={handleDropdownEnter}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <div className="h-px bg-[#DF631E]" />
                      <div className="p-8">
                        <div className="flex items-center gap-3 mb-6">
                          <span className="h-px w-8 bg-[#DF631E]" />
                          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                            {t('nav.products')}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-10 gap-y-0">
                          {productCategories.map((category) => (
                            <CategoryLink key={category.key} category={category} />
                          ))}
                        </div>
                        <div className="mt-6 pt-6 border-t border-[#4E2118]/8">
                          <LocalizedLink
                            to="products"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#DF631E] hover:text-[#B85018] transition-colors duration-300"
                          >
                            {t('buttons.discover')} {t('nav.products')}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </LocalizedLink>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
          <div className="ml-4 pl-4 border-l border-current/20">
            <LanguageSelector variant="desktop" isScrolled={isScrolled || isMobileOpen} />
          </div>
        </div>

        {/* Mobile controls */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageSelector variant="desktop" isScrolled={isScrolled || isMobileOpen} />
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={cn(
              'p-2 transition-all duration-300',
              isTransparent
                ? 'text-white hover:bg-white/10'
                : 'text-[#4E2118] hover:bg-[#4E2118]/5'
            )}
            aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* ============ MOBILE MENU ============ */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-x-0 top-20 md:top-24 bg-white z-40 overflow-y-auto border-t border-[#4E2118]/10"
            style={{ maxHeight: 'calc(100vh - 5rem)' }}
          >
            <nav
              className="flex flex-col py-4"
              aria-label={t('aria.mobileNavigation')}
            >
              {/* Products accordion */}
              <div className="px-4">
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className={cn(
                    'flex items-center justify-between w-full px-4 py-3 text-base font-medium transition-colors',
                    isActiveRoute('products')
                      ? 'text-[#4E2118]'
                      : 'text-[#4E2118]/70'
                  )}
                >
                  {t('nav.products')}
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 transition-transform duration-300',
                      mobileProductsOpen && 'rotate-180'
                    )}
                  />
                </button>

                <AnimatePresence>
                  {mobileProductsOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-4 border-l border-[#4E2118]/10 pl-4 pb-2 space-y-0.5">
                        {productCategories.map((category) => {
                          const slug = categoryToProductSlug[category.key]
                          const cls = "block px-4 py-2 text-sm text-[#4E2118]/60 hover:text-[#4E2118] transition-colors"
                          return slug ? (
                            <Link
                              key={category.key}
                              to={`/${lang}/${productsPath}/${slug}`}
                              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                              className={cls}
                            >
                              {t(category.labelKey)}
                            </Link>
                          ) : (
                            <LocalizedLink
                              key={category.key}
                              to="products"
                              className={cls}
                            >
                              {t(category.labelKey)}
                            </LocalizedLink>
                          )
                        })}
                        <LocalizedLink
                          to="products"
                          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#DF631E]"
                        >
                          {t('buttons.discover')} {t('nav.products')}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </LocalizedLink>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other nav items */}
              {navItems.slice(1).map(({ key, labelKey }) => (
                <LocalizedLink
                  key={key}
                  to={key}
                  className={cn(
                    'mx-4 px-4 py-3 text-base font-medium transition-colors',
                    isActiveRoute(key)
                      ? 'text-[#4E2118]'
                      : 'text-[#4E2118]/70 hover:text-[#4E2118]'
                  )}
                >
                  {t(labelKey)}
                </LocalizedLink>
              ))}

              {/* Language */}
              <div className="border-t border-[#4E2118]/10 mt-4 pt-4 px-4">
                <p className="px-4 text-xs font-semibold text-[#4E2118]/40 uppercase tracking-[0.2em] mb-2">
                  {t('aria.language') || 'Lingua'}
                </p>
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
